// src/pages/Admin.jsx
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export default function Admin() {
  const [studies, setStudies] = useState([]);
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);               // approved users
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const navigate = useNavigate();

  // Helper: authenticated fetch — relies on httpOnly cookie (no localStorage)
  const authFetch = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      credentials: 'include', // sends the httpOnly sb_token cookie automatically
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
    if (res.status === 401 || res.status === 403) {
      navigate('/login', { replace: true });
      throw new Error('Unauthorized');
    }
    return res;
  };

  // Data loaders
  const fetchStudies = async () => {
    const res = await fetch('/api/studies');
    const data = await res.json();
    setStudies(data.reverse());
  };

  // If this succeeds, we *know* the user is admin; mark it true.
  const fetchPending = async () => {
    try {
      const res = await authFetch('/api/pending-users');
      if (res.ok) {
        const data = await res.json();
        setPending(data);
        setIsAdmin(true); // <-- success on admin-only route
      } else {
        setPending([]);
      }
    } catch {
      setPending([]);
    }
  };

  // Same here: success => admin
  const fetchUsers = async () => {
    try {
      const res = await authFetch('/api/users');
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
        setIsAdmin(true); // <-- success on admin-only route
      } else {
        setUsers([]);
      }
    } catch {
      setUsers([]);
    }
  };


  useEffect(() => {
    // Verify session by hitting an auth-protected endpoint; redirect to login if not authenticated
    fetch('/api/auth/check', { credentials: 'include' }).then((r) => {
      if (!r.ok) { navigate('/login', { replace: true }); return; }
      setAuthorized(true);
      fetchStudies();
      fetchPending();
      fetchUsers();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Form handlers
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async () => {
    const res = await authFetch('/api/studies/add', {
      method: 'POST',
      body: JSON.stringify(form),
    });

    if (res.ok) {
      setMsg('Study added!');
      setForm({});
      fetchStudies();
    } else {
      setMsg('Failed to add study.');
    }
  };

  // Studies actions
  const remove = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this study?');
    if (!confirmDelete) return;
    const res = await authFetch(`/api/studies/${id}`, { method: 'DELETE' });
    if (res.ok) {
      setStudies((prev) => prev.filter((s) => s.id !== id));
    } else {
      alert('Failed to delete study');
    }
  };

  // Pending actions
  const approve = async (id) => {
    await authFetch(`/api/approve/${id}`, { method: 'POST' });
    fetchPending();
    fetchUsers(); // approved user moves into users list
  };

  const deny = async (id) => {
    await authFetch(`/api/deny/${id}`, { method: 'DELETE' });
    fetchPending();
  };

  // Users actions
  const revoke = async (id) => {
    if (!window.confirm("Remove this user's access?")) return;
    const res = await authFetch(`/api/users/${id}`, { method: 'DELETE' });
    if (res.ok) {
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } else {
      alert('Failed to remove user');
    }
  };

  const promote = async (id) => {
    const res = await authFetch(`/api/users/${id}/promote`, { method: 'POST' });
    if (res.ok) setUsers((prev) => prev.map((u) => u.id === id ? { ...u, is_admin: true } : u));
    else alert('Failed to promote user');
  };

  const demote = async (id) => {
    const res = await authFetch(`/api/users/${id}/demote`, { method: 'POST' });
    if (res.ok) setUsers((prev) => prev.map((u) => u.id === id ? { ...u, is_admin: false } : u));
    else alert(await res.text());
  };

  const fields = [
    "Age_category", "Authors", "Article_title", "Journal",
    "Year_of_publication", "DOI", "Country", "Study_type",
    "Sample_size_Healthy_control", "Sample_size_Patients", "Age", "Epilepsy_type",
    "Experimental_groups", "Sample_type", "Host", "Method", "DNA_isolation_kit",
    "Sequencing_platform", "Sequencing_primers", "Data_accession_ID", "Data_accession_link",
    "Increased_taxa", "Decreased_taxa", "Increased_metabolite", "Decreased_metabolite"
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  if (!authorized) return null;

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      style={{
        padding: '2rem',
        maxWidth: '1400px',
        margin: '0 auto',
        fontFamily: '"Inter", sans-serif',
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
        minHeight: "100vh",
      }}
    >
      <motion.h2
        variants={itemVariants}
        style={{
          color: '#0079C0',
          fontSize: '2.5rem',
          marginBottom: '2rem',
          textAlign: 'center',
          fontWeight: "700",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
        }}
      >
        Admin Dashboard
      </motion.h2>

      {/* Add Study Form */}
      <motion.div
        variants={itemVariants}
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '16px',
          padding: '2.5rem',
          marginBottom: '3rem',
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1.5rem',
        }}
      >
        {fields.map((f) => (
          <motion.div key={f} variants={itemVariants} style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor={f} style={{
              fontWeight: '600', fontSize: '0.95rem', marginBottom: '0.5rem',
              color: "#374151", display: "flex", alignItems: "center", gap: "0.5rem",
            }}>
              {f.replace(/_/g, ' ')}
            </label>
            <input
              name={f}
              value={form[f] || ''}
              placeholder={`Enter ${f.replace(/_/g, ' ')}`}
              onChange={handleChange}
              style={{
                padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db',
                fontSize: "0.9rem", background: "white", transition: "all 0.3s ease",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#0079C0";
                e.target.style.boxShadow = "0 0 0 3px rgba(0, 121, 192, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
              }}
            />
          </motion.div>
        ))}
        <motion.div variants={itemVariants} style={{ gridColumn: '1 / -1', textAlign: 'center', marginTop: '2rem' }}>
          <motion.button
            onClick={submit}
            style={{
              padding: '1rem 2.5rem',
              background: "linear-gradient(135deg, #0079C0 0%, #005f9e 100%)",
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: "1rem",
              boxShadow: "0 8px 25px rgba(0, 121, 192, 0.3)",
              transition: "all 0.3s ease",
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Add Study
          </motion.button>
          <AnimatePresence>
            {msg && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                style={{
                  marginTop: '1rem',
                  color: msg.toLowerCase().includes('success') || msg.toLowerCase().includes('added') ? '#10b981' : '#ef4444',
                  fontWeight: "500",
                  fontSize: "0.95rem",
                }}
              >
                {msg}
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>

      {/* ===== Approved Users ===== */}
      <motion.div variants={itemVariants} style={{ marginBottom: '3rem' }}>
        <motion.h3
          variants={itemVariants}
          style={{
            marginBottom: '1.5rem',
            color: '#0079C0',
            fontSize: "1.5rem",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          Approved Users (Have Access)
        </motion.h3>

        {users.length === 0 ? (
          <motion.p
            variants={itemVariants}
            style={{
              marginTop: '1rem',
              padding: "2rem",
              background: "rgba(255, 255, 255, 0.8)",
              borderRadius: "12px",
              textAlign: "center",
              color: "#6b7280",
              fontSize: "1rem",
            }}
          >
            No approved users found.
          </motion.p>
        ) : (
          <motion.div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: "linear-gradient(135deg, #0079C0 0%, #005f9e 100%)", color: "white" }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: "600" }}>Username</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>First</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>Last</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>Email</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>Institution</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>Role</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr
                    key={u.id}
                    style={{
                      borderBottom: '1px solid #e5e7eb',
                      background: i % 2 === 0 ? "rgba(255, 255, 255, 0.8)" : "rgba(248, 250, 252, 0.8)",
                    }}
                  >
                    <td style={{ padding: '1rem', fontWeight: 500 }}>{u.username}</td>
                    <td style={{ padding: '1rem' }}>{u.first_name || '—'}</td>
                    <td style={{ padding: '1rem' }}>{u.last_name || '—'}</td>
                    <td style={{ padding: '1rem' }}>{u.email || '—'}</td>
                    <td style={{ padding: '1rem' }}>{u.host_institution || '—'}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.2rem 0.6rem',
                        borderRadius: 4,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: u.is_admin ? 'rgba(0,121,192,0.1)' : 'rgba(100,116,139,0.1)',
                        color: u.is_admin ? '#0079C0' : '#64748b',
                      }}>
                        {u.is_admin ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {u.is_admin ? (
                        <button
                          onClick={() => demote(u.id)}
                          style={{ background: '#f59e0b', color: 'white', padding: '0.4rem 0.75rem', border: 'none', borderRadius: 6, fontWeight: 500, fontSize: '0.8rem', cursor: 'pointer' }}
                        >
                          Revoke Admin
                        </button>
                      ) : (
                        <button
                          onClick={() => promote(u.id)}
                          style={{ background: '#0079C0', color: 'white', padding: '0.4rem 0.75rem', border: 'none', borderRadius: 6, fontWeight: 500, fontSize: '0.8rem', cursor: 'pointer' }}
                        >
                          Make Admin
                        </button>
                      )}
                      <button
                        onClick={() => revoke(u.id)}
                        style={{ background: '#ef4444', color: 'white', padding: '0.4rem 0.75rem', border: 'none', borderRadius: 6, fontWeight: 500, fontSize: '0.8rem', cursor: 'pointer' }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}
      </motion.div>

      {/* ===== Pending Users ===== */}
      <motion.div variants={itemVariants} style={{ marginBottom: '3rem' }}>
        <motion.h3
          variants={itemVariants}
          style={{
            color: '#0079C0',
            fontSize: "1.5rem",
            fontWeight: "700",
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          Pending User Approvals
        </motion.h3>
        {pending.length === 0 ? (
          <motion.p
            variants={itemVariants}
            style={{
              marginTop: '1rem',
              padding: "2rem",
              background: "rgba(255, 255, 255, 0.8)",
              borderRadius: "12px",
              textAlign: "center",
              color: "#6b7280",
              fontSize: "1rem",
            }}
          >
            No pending users.
          </motion.p>
        ) : (
          <motion.div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <table style={{ width: '100%', marginTop: '1rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: "linear-gradient(135deg, #0079C0 0%, #005f9e 100%)", color: "white" }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: "600" }}>Username</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>First</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>Last</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>Email</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>Institution</th>
                  <th style={{ padding: '1rem', fontWeight: "600" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {pending.map((user, index) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: index * 0.05 }}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        background: index % 2 === 0 ? "rgba(255, 255, 255, 0.8)" : "rgba(248, 250, 252, 0.8)",
                      }}
                    >
                      <td style={{ padding: '1rem', fontWeight: "500" }}>{user.username}</td>
                      <td style={{ padding: '1rem' }}>{user.first_name || '—'}</td>
                      <td style={{ padding: '1rem' }}>{user.last_name || '—'}</td>
                      <td style={{ padding: '1rem' }}>{user.email || '—'}</td>
                      <td style={{ padding: '1rem' }}>{user.host_institution || '—'}</td>
                      <td style={{ padding: '1rem' }}>
                        <motion.button
                          onClick={() => approve(user.id)}
                          style={{
                            marginRight: '0.75rem',
                            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                            color: 'white',
                            padding: '0.5rem 1rem',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: "500",
                            fontSize: "0.9rem",
                            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                            transition: "all 0.3s ease",
                          }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Approve
                        </motion.button>
                        <motion.button
                          onClick={() => deny(user.id)}
                          style={{
                            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                            color: 'white',
                            padding: '0.5rem 1rem',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: "500",
                            fontSize: "0.9rem",
                            boxShadow: "0 4px 15px rgba(239, 68, 68, 0.3)",
                            transition: "all 0.3s ease",
                          }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Deny
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </motion.div>
        )}
      </motion.div>

      {/* ===== Current Studies ===== */}
      <div style={{ marginBottom: '3rem' }}>
        <h3
          style={{
            marginBottom: '1.5rem',
            color: '#0079C0',
            fontSize: "1.5rem",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          Current Studies
        </h3>
        <div
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg, #0079C0 0%, #005f9e 100%)", color: "white" }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: "600" }}>Title</th>
                <th style={{ padding: '1rem', fontWeight: "600" }}>Authors</th>
                <th style={{ padding: '1rem', fontWeight: "600" }}>Journal</th>
                <th style={{ padding: '1rem', fontWeight: "600" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {studies.map((study, index) => (
                <tr
                  key={study.id}
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    background: index % 2 === 0 ? "rgba(255, 255, 255, 0.8)" : "rgba(248, 250, 252, 0.8)",
                  }}
                >
                  <td style={{ padding: '1rem', fontWeight: "500" }}>{study.Article_title || '—'}</td>
                  <td style={{ padding: '1rem' }}>{study.Authors || '—'}</td>
                  <td style={{ padding: '1rem' }}>{study.Journal || '—'}</td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    {isAdmin && (
                      <button
                        onClick={() => remove(study.id)}
                        style={{
                          background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                          fontWeight: "500",
                          fontSize: "0.9rem",
                          boxShadow: "0 4px 15px rgba(239, 68, 68, 0.3)",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </motion.div>
  );
}